'use client';

import { useIsClient } from 'usehooks-ts';
import { useAppModal } from '@/hooks/useAppModal';
import { useRouter } from '@/hooks/useRouter';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useCallback } from 'react';
import { Link } from '@/app/[lang]/(main)/components/shared/PrivateLink/ServerLink';
import { useIsLoading } from '@/contexts/LoginContext';
import { sendGTMEvent } from '@next/third-parties/google';
import { useLocale } from 'next-intl';
import { Skeleton } from '@/components/ui/skeleton';

type Props = {
  className: string;
  text?: string;
  children?: React.ReactNode;
  eventName?: string;
  onClick?: () => void;
  linkHref?: string;
};

export const AppPromotionButton = ({ className, text, children, eventName, onClick, linkHref }: Props) => {
  const openModal = useAppModal();
  const router = useRouter();
  const isLoading = useIsLoading();
  const locale = useLocale();
  const hasMounted = useIsClient();

  const click = useCallback(() => {
    sendGTMEvent({ event: eventName });
    if (linkHref) {
      router.push(linkHref);
      return;
    }
    if (onClick) {
      onClick();
    } else {
      openModal();
    }
  }, [openModal, eventName, onClick, router, linkHref]);

  const dir = locale === 'he' ? 'rtl' : 'ltr';
  if (!hasMounted || isLoading) {
    return <Skeleton className='lg:block h-[44px] w-[160px] hidden' />;
  }
  return (
    <>
      {linkHref ? (
        <Button asChild variant='default' className={cn('h-auto', className)}>
          <Link href={linkHref} onClick={click} dir={dir}>
            {text ? text : children}
          </Link>
        </Button>
      ) : (
        <Button variant='default' onClick={click} className={cn('h-auto', className)} dir={dir}>
          {text ? text : children}
        </Button>
      )}
    </>
  );
};
